(() => {
  'use strict';
  if (window.WellnessMerge) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editor = Boolean(window.Shopify && window.Shopify.designMode);
  const observed = new WeakSet();
  const counted = new WeakSet();
  const initialized = new WeakSet();
  const documentRoot = document.documentElement;
  let videoDialog;
  let scrollQueued = false;

  function each(root, selector, fn) {
    if (root.matches && root.matches(selector)) fn(root);
    root.querySelectorAll(selector).forEach(fn);
  }

  function count(element) {
    if (counted.has(element)) return;
    counted.add(element);
    const target = Number(element.dataset.ccCount);
    if (Number.isFinite(target)) element.textContent = String(target);
  }

  function reveal(element) {
    element.classList.remove('cc-pending');
    element.classList.add('cc-in-view');
    each(element, '[data-cc-count]', count);
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      reveal(entry.target);
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }) : null;

  function splitHeading(heading) {
    if (heading.dataset.ccSplit || reduced.matches || editor || heading.closest('dialog')) return;
    heading.dataset.ccSplit = 'true';
    if (!heading.hasAttribute('aria-label')) heading.setAttribute('aria-label', heading.textContent.trim());
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let index = 0;
    nodes.forEach(node => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach(word => {
        if (!word || /^\s+$/.test(word)) {
          fragment.append(document.createTextNode(word));
          return;
        }
        const outer = document.createElement('span');
        const inner = document.createElement('span');
        outer.className = 'cc-word';
        inner.className = 'cc-word-inner';
        outer.setAttribute('aria-hidden', 'true');
        Array.from(word).forEach(character => {
          const glyph = document.createElement('span');
          glyph.className = 'cc-char';
          glyph.style.setProperty('--cc-char-delay', `${Math.min(index++, 32) * 14}ms`);
          glyph.textContent = character;
          inner.append(glyph);
        });
        outer.append(inner);
        fragment.append(outer);
      });
      node.replaceWith(fragment);
    });
  }

  function prepare(root = document) {

    each(root, '[data-cc-reveal]', element => {
      if (observed.has(element)) return;
      observed.add(element);
      if (element.parentElement?.classList.contains('cc-grid')) {
        const index = [...element.parentElement.children].indexOf(element);
        element.style.setProperty('--cc-reveal-delay', `${Math.min(index % 4, 3) * 70}ms`);
      }
      reveal(element);
    });
    each(root, '[data-cc-date]', input => {
      const now = new Date();
      const pad = number => String(number).padStart(2, '0');
      input.min = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    });
    each(root, 'dialog.cc-dialog', connectDialog);
    updateScroll();
  }

  function closeVideo() {
    if (!videoDialog) return;
    videoDialog.querySelectorAll('iframe').forEach(frame => frame.remove());
  }

  function connectDialog(dialog) {
    if (initialized.has(dialog)) return;
    initialized.add(dialog);
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    dialog.addEventListener('cancel', () => { if (dialog === videoDialog) closeVideo(); });
    dialog.addEventListener('close', () => {
      if (dialog === videoDialog) closeVideo();
      if (!document.querySelector('dialog.cc-dialog[open]')) documentRoot.classList.remove('cc-modal-open');
    });
  }

  function showDialog(dialog) {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    connectDialog(dialog);
    if (!dialog.open) dialog.showModal();
    documentRoot.classList.add('cc-modal-open');
  }

  function showVideo(button) {
    const id = button.dataset.ccVideoId;
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) return;
    const provider = button.dataset.ccVideoProvider;
    const source = provider === 'vimeo'
      ? `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1`
      : `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
    if (!videoDialog) {
      videoDialog = document.createElement('dialog');
      videoDialog.className = 'cc cc-dialog cc-video-dialog';
      videoDialog.setAttribute('aria-label', 'Clinic introduction video');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'cc-dialog-close';
      close.dataset.ccClose = '';
      close.setAttribute('aria-label', 'Close video');
      close.textContent = '×';
      videoDialog.append(close);
      document.body.append(videoDialog);
    }
    closeVideo();
    const iframe = document.createElement('iframe');
    iframe.title = 'Clinic introduction video';
    iframe.src = source;
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allowFullscreen = true;
    videoDialog.append(iframe);
    showDialog(videoDialog);
  }

  function updateScroll() {
    scrollQueued = false;
    const compact = documentRoot.classList.contains('cc-scrolled');
    documentRoot.classList.toggle('cc-scrolled', compact ? window.scrollY > 16 : window.scrollY > 72);
    document.querySelectorAll('[data-cc-top]').forEach(button => { button.hidden = window.scrollY < 650; });
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    const videoButton = target.closest('[data-cc-video-id]');
    const openButton = target.closest('[data-cc-open]');
    const closeButton = target.closest('[data-cc-close]');
    const topButton = target.closest('[data-cc-top]');
    if (videoButton) { showVideo(videoButton); return; }
    if (openButton) { showDialog(document.getElementById(openButton.dataset.ccOpen)); return; }
    if (closeButton) { const dialog = closeButton.closest('dialog'); if (dialog === videoDialog) closeVideo(); dialog?.close(); return; }
    if (topButton) { window.scrollTo({ top: 0, behavior: reduced.matches ? 'auto' : 'smooth' }); return; }
    const link = target.closest('.cc a[href^="#cc-"]');
    if (link && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      const section = document.getElementById(link.hash.slice(1));
      if (section) {
        event.preventDefault();
        if (link.closest('dialog')) link.closest('dialog').close();
        document.querySelectorAll('[data-cc-mobile][open], .cc-nav-sub[open], .cc-mobile-nav-sub[open]').forEach(menu => { menu.open = false; });
        history.pushState(null, '', link.hash);
        section.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
        section.setAttribute('tabindex', '-1');
        section.focus({ preventScroll: true });
        section.addEventListener('blur', () => section.removeAttribute('tabindex'), { once: true });
      }
    }
    document.querySelectorAll('[data-cc-mobile][open], .cc-nav-sub[open], .cc-mobile-nav-sub[open]').forEach(menu => {
      if (!menu.contains(target)) menu.open = false;
    });
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('[data-cc-mobile][open], .cc-nav-sub[open], .cc-mobile-nav-sub[open]').forEach(menu => { menu.open = false; });
  });
  window.addEventListener('scroll', () => {
    if (!scrollQueued) { scrollQueued = true; requestAnimationFrame(updateScroll); }
  }, { passive: true });
  window.addEventListener('pagehide', closeVideo);
  reduced.addEventListener('change', () => {
    if (!reduced.matches) return;
    document.querySelectorAll('.cc-pending').forEach(reveal);
    documentRoot.classList.remove('cc-motion');
  });
  document.addEventListener('shopify:section:load', event => prepare(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (observer) each(event.target, '[data-cc-reveal]', element => observer.unobserve(element));
  });
  document.addEventListener('shopify:block:select', event => {
    const details = event.target.closest('.cc-faq-item');
    if (details) details.open = true;
    const slide = event.target.closest('[data-cc-slide]');
    if (slide) slide.closest('cc-testimonials')?.showBlock(slide.dataset.blockId);
  });

  if (!customElements.get('cc-testimonials')) {
    customElements.define('cc-testimonials', class extends HTMLElement {
      connectedCallback() {
        if (this.connected) return;
        this.connected = true;
        this.index = 0;
        this.timer = null;
        this.userPaused = false;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.slides = [...this.querySelectorAll('[data-cc-slide]')];
        this.previous = this.querySelector('[data-cc-prev]');
        this.next = this.querySelector('[data-cc-next]');
        this.status = this.querySelector('[data-cc-slider-status]');
        this.pauseButton = this.querySelector('[data-cc-pause]');
        this.previous?.addEventListener('click', () => this.go(this.index - 1, true), options);
        this.next?.addEventListener('click', () => this.go(this.index + 1, true), options);
        this.pauseButton?.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.pauseButton.textContent = this.userPaused ? 'Play' : 'Pause';
          this.pauseButton.setAttribute('aria-label', this.userPaused ? 'Play automatic slides' : 'Pause automatic slides');
          this.start();
        }, options);
        this.addEventListener('mouseenter', () => this.stop(), options);
        this.addEventListener('mouseleave', () => this.start(), options);
        this.addEventListener('focusin', () => this.stop(), options);
        this.addEventListener('focusout', () => { setTimeout(() => this.isConnected && this.start(), 0); }, options);
        document.addEventListener('visibilitychange', () => this.start(), options);
        reduced.addEventListener('change', () => this.start(), options);
        this.addEventListener('pointerdown', event => { this.touchX = event.clientX; }, options);
        this.addEventListener('pointerup', event => {
          if (typeof this.touchX !== 'number') return;
          const distance = event.clientX - this.touchX;
          this.touchX = null;
          if (Math.abs(distance) > 60 && !String(window.getSelection())) this.go(this.index + (distance < 0 ? 1 : -1), true);
        }, options);
        this.go(0, false);
        this.start();
      }
      disconnectedCallback() {
        this.stop();
        this.controller?.abort();
        this.connected = false;
      }
      go(index, manual = false) {
        const length = this.slides.length;
        if (!length) {
          if (this.status) this.status.textContent = '0 / 0';
          if (this.previous) this.previous.disabled = true;
          if (this.next) this.next.disabled = true;
          return;
        }
        this.index = (index + length) % length;
        this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
        if (this.status) this.status.textContent = `${this.index + 1} / ${length}`;
        if (this.previous) this.previous.disabled = length < 2;
        if (this.next) this.next.disabled = length < 2;
        if (manual) this.start();
      }
      showBlock(blockId) {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === blockId);
        if (index >= 0) { this.go(index); this.stop(); }
      }
      stop() { clearInterval(this.timer); this.timer = null; }
      start() {
        this.stop();
        if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || this.userPaused || document.hidden || reduced.matches || editor || this.matches(':hover, :focus-within')) return;
        this.timer = setInterval(() => this.go(this.index + 1), 7000);
      }
    });
  }

  window.WellnessMerge = { init: prepare };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepare(), { once: true });
  else prepare();
})();
